import React, { useState } from 'react';
import { Container, Row, Col, Button, Form } from 'react-bootstrap';
import { 
  FaMapMarkerAlt, FaEnvelope, FaPhone, FaCalendarAlt,
  FaGithub, FaLinkedin, FaWhatsapp
} from 'react-icons/fa';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null); // { type: 'success'|'error', message: string }
  const [submitting, setSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Please enter your name';
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.email.trim() || !emailPattern.test(form.email)) newErrors.email = 'Enter a valid email address';
    if (!form.message.trim()) newErrors.message = 'Please enter a brief message';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    setStatus(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: form.message,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: 'success', message: 'Thank you! Your message has been sent successfully.' });
        setForm({ name: '', email: '', message: '' });
        setErrors({});
      } else {
        setStatus({ type: 'error', message: data.message || 'Something went wrong. Please try again.' });
      }
    } catch {
      setStatus({ type: 'error', message: 'Network error. Please check your connection and try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section">
      <Container>
        <div className="section-title">
          <h2>Contact Us</h2>
          <p className="lead text-center mb-5">
            Ready to discuss your project? We're here to help you bring your vision to life.
          </p>
        </div>

        <Row className="justify-content-center">
          <Col lg={10} className="mb-5">
            <div className="contact-card p-0 bg-white rounded shadow">
              <div className="row g-0">
                <div className="col-md-5 bg-primary text-white p-4 d-flex flex-column justify-content-center">
                  <div className="px-4 py-5">
                    <h3 className="mb-4 fw-bold">Get in Touch</h3>
                    <p className="mb-5 opacity-75">
                      We're always looking for new projects and opportunities. If you'd like to work with us, 
                      please get in touch with us today.
                    </p>
                    <div className="mt-5 pt-4">
                      <div className="d-flex align-items-center mb-4">
                        <div className="icon me-3">
                          <FaEnvelope size={20} />
                        </div>
                        <a href="mailto:info@gdwd.co.uk" className="text-white text-decoration-none">info@gdwd.co.uk</a>
                      </div>
                      <div className="d-flex align-items-center">
                        <div className="icon me-3">
                          <FaMapMarkerAlt size={20} />
                        </div>
                        <p className="mb-0">Liepdārzi 15, Baldones nov. LV-2125</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-7 p-5">
                  <h4 className="mb-4 fw-bold">Contact Information</h4>
                  <div className="contact-info">
                    <div className="info-item d-flex mb-4">
                      <div className="icon me-3 text-primary">
                        <FaPhone size={24} />
                      </div>
                      <div>
                        <h5 className="fw-bold">Phone</h5>
                        <p className="mb-0">Contact us via email for phone details</p>
                      </div>
                    </div>
                    
                    <div className="info-item d-flex mb-4">
                      <div className="icon me-3 text-primary">
                        <FaEnvelope size={24} />
                      </div>
                      <div>
                        <h5 className="fw-bold">Email</h5>
                        <p className="mb-0"><a href="mailto:info@gdwd.co.uk" className="text-decoration-none" style={{ color: '#3498db' }}>info@gdwd.co.uk</a></p>
                      </div>
                    </div>
                    
                    <div className="info-item d-flex mb-4">
                      <div className="icon me-3 text-primary">
                        <FaMapMarkerAlt size={24} />
                      </div>
                      <div>
                        <h5 className="fw-bold">Location</h5>
                        <p className="mb-0">Liepdārzi 15, Baldones nov. LV-2125</p>
                      </div>
                    </div>
                    
                    <div className="info-item d-flex mb-4">
                      <div className="icon me-3 text-primary">
                        <FaCalendarAlt size={24} />
                      </div>
                      <div>
                        <h5 className="fw-bold">Business Hours</h5>
                        <p className="mb-1">Monday - Friday: 9:00 AM - 6:00 PM</p>
                        <p className="mb-1">Saturday: By appointment</p>
                        <p className="mb-0">Sunday: Closed</p>
                      </div>
                    </div>
                    <hr className="my-4" />
                    <h4 className="mb-3 fw-bold">Send us a message</h4>

                    {status && (
                      <div className={`form-status ${status.type}`} role="alert">
                        {status.message}
                      </div>
                    )}

                    <Form noValidate onSubmit={handleSubmit} aria-label="Contact form">
                      <Form.Group className="mb-3" controlId="contactName">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Your name"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          isInvalid={!!errors.name}
                        />
                        <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                      </Form.Group>
                      <Form.Group className="mb-3" controlId="contactEmail">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="you@example.com"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          isInvalid={!!errors.email}
                        />
                        <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                      </Form.Group>
                      <Form.Group className="mb-3" controlId="contactMessage">
                        <Form.Label>Message</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          placeholder="How can we help?"
                          value={form.message}
                          onChange={(e) => setForm({ ...form, message: e.target.value })}
                          isInvalid={!!errors.message}
                        />
                        <Form.Control.Feedback type="invalid">{errors.message}</Form.Control.Feedback>
                      </Form.Group>
                      <div className="d-grid">
                        <Button type="submit" variant="primary" disabled={submitting}>
                          {submitting ? 'Sending…' : 'Send Message'}
                        </Button>
                      </div>
                    </Form>

                    <div className="mt-5">
                      <h5 className="fw-bold mb-3">Connect With Us</h5>
                      <div className="d-flex gap-3">
                        <a 
                          href="https://github.com/guntarsdikis" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="social-link"
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            transition: 'transform 0.3s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px) scale(1.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0) scale(1)'}
                          aria-label="GitHub"
                        >
                          <FaGithub size={20} />
                        </a>
                        <a 
                          href="https://www.linkedin.com/in/guntars-dikis-a85b6042/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="social-link"
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #0077b5, #0e76a8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            transition: 'transform 0.3s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px) scale(1.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0) scale(1)'}
                          aria-label="LinkedIn"
                        >
                          <FaLinkedin size={20} />
                        </a>
                        <a 
                          href="https://wa.me/37129438427" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="social-link"
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #25d366, #128c7e)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            transition: 'transform 0.3s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px) scale(1.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0) scale(1)'}
                          aria-label="WhatsApp"
                        >
                          <FaWhatsapp size={20} />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Contact;
